import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBoxOpen,
  FaCheck,
  FaCheckCircle,
  FaCloudDownloadAlt,
  FaExclamationTriangle,
  FaFolderOpen,
  FaImage,
  FaImages,
  FaInfoCircle,
  FaLayerGroup,
  FaSearch,
  FaSyncAlt,
  FaTimes,
} from "react-icons/fa";
import "./StoreImporter.css";

const emptyCounts = {
  categories_created: 0,
  categories_updated: 0,
  products_created: 0,
  products_updated: 0,
  products_skipped: 0,
  images_stored: 0,
  errors: [],
};

const BATCH_SIZE = 5;
const PREVIEW_SIZE = 10;

const newProductStore = () => ({ items: [], seen: new Set(), next: null, total: 0, started: false });

/**
 * The catalog half of a store import page, shared by Shopify and WooCommerce:
 * fetching collections and products page by page, the preview, the streamed
 * import, progress, and results. Each page passes its own connection form.
 *
 * source: { name, Icon, api, colors: { accent, dark, light, soft, ring },
 *           subtitle, groups: "collections" | "categories", connectHint }
 * connection: { status, ready, storeName }  status: loading | connected | error | other
 */
export default function StoreImporter({ source, connection, connecting, children }) {
  const groups = source.groups;
  const groupsTitle = groups.charAt(0).toUpperCase() + groups.slice(1);
  const ready = !!connection.ready;

  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [limitations, setLimitations] = useState([]);
  const [phase, setPhase] = useState("");
  const [progress, setProgress] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [productMeta, setProductMeta] = useState({ next: null, total: 0, started: false });
  const [loadingPage, setLoadingPage] = useState(false);
  const startedAt = useRef(0);
  const productStore = useRef(newProductStore());
  const inflight = useRef(new Map());

  const busy = phase !== "";
  const locked = busy || connecting;

  useEffect(() => {
    if (!busy) return undefined;
    startedAt.current = Date.now();
    setElapsed(0);
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [busy]);

  // Warn before leaving the page in the middle of an import.
  useEffect(() => {
    if (phase !== "import") return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [phase]);

  const fetchAllCollections = async () => {
    const rows = [];
    const seen = new Set();
    let cursor = null;
    let total = 0;
    const seenCursors = new Set();
    setProgress({ label: `Reading ${groups}`, done: 0, total: 0, unit: groups });
    do {
      if (cursor) seenCursors.add(cursor);
      const response = await axios.post(`${source.api}/fetch`, { resource: "collections", cursor });
      total = response.data.total || total;
      let added = 0;
      (response.data.collections || []).forEach((item) => {
        const id = String(item.id);
        if (seen.has(id)) return;
        seen.add(id);
        rows.push(item);
        added += 1;
      });
      setCollections([...rows]);
      setProgress({ label: `Reading ${groups}`, done: rows.length, total, unit: groups });
      const next = response.data.next_cursor || null;
      if (added === 0 || !next || seenCursors.has(next)) break;
      cursor = next;
    } while (cursor);
    return rows;
  };

  // Loads one source page of products and appends it. A page already being
  // requested returns the same promise, so prefetch, Next, and the import
  // never fetch a page twice.
  const fetchProductPage = (cursor) => {
    const key = cursor || "first";
    if (inflight.current.has(key)) return inflight.current.get(key);
    const store = productStore.current;
    const request = axios.post(`${source.api}/fetch`, { resource: "products", cursor }).then((response) => {
      if (store !== productStore.current) return response.data;
      (response.data.products || []).forEach((item) => {
        const id = String(item.id);
        if (store.seen.has(id)) return;
        store.seen.add(id);
        store.items.push(item);
      });
      const next = response.data.next_cursor || null;
      store.next = next && next !== cursor ? next : null;
      store.total = response.data.total || store.total;
      store.started = true;
      setProducts([...store.items]);
      setProductMeta({ next: store.next, total: store.total, started: true });
      setLimitations(response.data.limitations || []);
      return response.data;
    }).finally(() => {
      inflight.current.delete(key);
    });
    inflight.current.set(key, request);
    return request;
  };

  const resetProducts = () => {
    productStore.current = newProductStore();
    inflight.current = new Map();
    setProducts([]);
    setProductMeta({ next: null, total: 0, started: false });
  };

  const loadCollections = async () => {
    setError("");
    setNotice("");
    setResult(null);
    setPhase("collections");
    try {
      const rows = await fetchAllCollections();
      setNotice(`Loaded ${rows.length} ${groups}.`);
    } catch (err) {
      setError(err.response?.data?.message || `The ${groups} could not be loaded.`);
    } finally {
      setProgress(null);
      setPhase("");
    }
  };

  const loadProducts = async () => {
    setError("");
    setNotice("");
    setResult(null);
    setPage(0);
    setQuery("");
    resetProducts();
    setPhase("products");
    try {
      const data = await fetchProductPage(null);
      const count = (data.products || []).length;
      const total = data.total || count;
      setNotice(total > count
        ? `Showing the first ${count} of ${total} products. More pages load as you browse.`
        : `Loaded ${count} ${count === 1 ? "product" : "products"}.`);
    } catch (err) {
      setError(err.response?.data?.message || "The products could not be loaded.");
    } finally {
      setPhase("");
    }
  };

  const goNext = async () => {
    const nextStart = (page + 1) * PREVIEW_SIZE;
    if (query.trim() || nextStart < products.length) {
      setPage(page + 1);
      return;
    }
    if (!productMeta.next) return;
    setLoadingPage(true);
    setPage(page + 1);
    try {
      await fetchProductPage(productMeta.next);
    } catch (err) {
      setPage(page);
      setError(err.response?.data?.message || "The next page could not be loaded.");
    } finally {
      setLoadingPage(false);
    }
  };

  // Prefetch the page after the one being viewed, so Next is instant.
  useEffect(() => {
    if (phase === "import" || query.trim() || !productMeta.next) return;
    if ((page + 2) * PREVIEW_SIZE <= products.length) return;
    fetchProductPage(productMeta.next).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, products.length, productMeta.next, phase, query]);

  // Imports page by page: the next source page downloads while the current
  // one is being saved, so the import starts right away.
  const importAll = async () => {
    setError("");
    setNotice("");
    setPhase("import");
    const totals = { ...emptyCounts, errors: [] };
    let processed = 0;
    try {
      const sourceCollections = collections.length ? collections : await fetchAllCollections();
      const store = productStore.current;
      if (!store.started) {
        setProgress({ label: "Reading products", done: 0, total: 0, unit: "products" });
        await fetchProductPage(null);
      }
      if (!store.items.length) {
        setError("The store returned no products to import.");
        return;
      }
      setResult({ ...totals });
      for (;;) {
        const pending = store.next ? fetchProductPage(store.next) : null;
        if (pending) pending.catch(() => {});
        while (processed < store.items.length) {
          const batch = store.items.slice(processed, processed + BATCH_SIZE);
          setProgress({ label: "Importing products", done: processed, total: Math.max(store.total, store.items.length), unit: "products", current: batch[0]?.title });
          // Each batch carries the groups its products use, so stores with more than 100 still map correctly.
          const used = new Set(batch.flatMap((product) => (product.collection_ids || []).map(String)));
          const usedCollections = sourceCollections.filter((item) => used.has(String(item.id)) || used.has(String(item.handle)));
          const extraCollections = processed === 0 ? sourceCollections.filter((item) => !usedCollections.includes(item)) : [];
          const response = await axios.post(`${source.api}/import`, {
            collections: usedCollections.concat(extraCollections).slice(0, 100),
            products: batch,
          });
          const data = response.data || {};
          if (processed === 0) {
            totals.categories_created += data.categories_created || 0;
            totals.categories_updated += data.categories_updated || 0;
          }
          totals.products_created += data.products_created || 0;
          totals.products_updated += data.products_updated || 0;
          totals.products_skipped += data.products_skipped || 0;
          totals.images_stored += data.images_stored || 0;
          totals.errors = totals.errors.concat(data.errors || []);
          processed += batch.length;
          setResult({ ...totals, errors: [...totals.errors] });
        }
        if (!pending) break;
        await pending;
      }
      setNotice(`Import finished: ${processed} products processed.`);
    } catch (err) {
      setError(err.response?.data?.message || `The import stopped after ${processed} products.`);
      setResult({ ...totals, errors: [...totals.errors] });
    } finally {
      setProgress(null);
      setPhase("");
    }
  };

  const groupTitles = useMemo(() => {
    const map = new Map();
    collections.forEach((item) => {
      map.set(String(item.id), item.title);
      if (item.handle) map.set(String(item.handle), item.title);
    });
    return map;
  }, [collections]);

  const groupName = (product) => (product.collection_ids || [])
    .map((ref) => groupTitles.get(String(ref)))
    .filter(Boolean)
    .join(", ") || (collections.length ? "Imported" : "—");

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return products;
    return products.filter((product) =>
      [product.title, product.handle, product.vendor, product.product_type].join(" ").toLowerCase().includes(needle)
    );
  }, [products, query]);

  const variantCount = useMemo(
    () => products.reduce((sum, product) => sum + (isVariable(product) ? (product.variants || []).length : 0), 0),
    [products]
  );
  const imageCount = useMemo(() => products.reduce((sum, product) => sum + (product.images || []).length, 0), [products]);

  const searching = query.trim() !== "";
  const knownTotal = searching ? filtered.length : Math.max(productMeta.total, products.length);
  const pageCount = Math.max(1, Math.ceil(knownTotal / PREVIEW_SIZE));
  const visible = filtered.slice(page * PREVIEW_SIZE, page * PREVIEW_SIZE + PREVIEW_SIZE);
  const hasNext = page + 1 < pageCount && (searching || (page + 1) * PREVIEW_SIZE < products.length || !!productMeta.next);
  const showSkeleton = !visible.length && (phase === "products" || loadingPage);

  let step = 2;
  if (!ready) step = 1;
  else if (phase === "import") step = 3;
  else if (result && !busy) step = 4;
  else if (products.length && !busy) step = 3;

  const Icon = source.Icon;
  const colors = source.colors;
  const style = {
    "--si-accent": colors.accent,
    "--si-accent-dark": colors.dark,
    "--si-accent-light": colors.light,
    "--si-accent-soft": colors.soft,
    "--si-accent-ring": colors.ring,
  };

  return (
    <div className="si-import" style={style}>
      <div className="si-hero">
        <div className="si-hero-main">
          <div className="si-hero-icon"><Icon /></div>
          <div>
            <h4>{source.name} import</h4>
            <p>{source.subtitle}</p>
          </div>
        </div>
        <StatusPill status={connection.status} label={connection.label} />
      </div>

      <div className="si-steps">
        <Step number={1} label="Connect store" hint={source.connectHint} current={step} />
        <Step number={2} label="Fetch catalog" hint={`Preview ${groups} and products`} current={step} />
        <Step number={3} label="Import" hint="Save into this store" current={step} />
      </div>

      {progress ? <ProgressPanel progress={progress} elapsed={elapsed} /> : null}

      {children}

      <div className="si-card">
        <div className="si-card-head">
          <div>
            <h5>Catalog</h5>
            <p className="si-card-sub">
              {ready ? `Preview what will be copied, or import everything in one go.` : `Connect the store first to read its catalog.`}
            </p>
          </div>
        </div>
        <div className="si-card-body">
          <div className="si-actions">
            <button type="button" className="si-btn" onClick={loadCollections} disabled={locked || !ready}>
              {phase === "collections" ? <Spinner /> : <FaFolderOpen />}
              {phase === "collections" ? "Fetching…" : `Fetch ${groupsTitle}`}
            </button>
            <button type="button" className="si-btn" onClick={loadProducts} disabled={locked || !ready}>
              {phase === "products" ? <Spinner /> : <FaBoxOpen />}
              {phase === "products" ? "Fetching…" : "Fetch Products"}
            </button>
            <button type="button" className="si-btn si-btn-primary" onClick={importAll} disabled={locked || !ready}>
              {phase === "import" ? <Spinner light /> : <FaCloudDownloadAlt />}
              {phase === "import" ? "Importing…" : "Import All"}
            </button>
          </div>
          {notice ? <Alert tone="success" onClose={() => setNotice("")}>{notice}</Alert> : null}
          {error ? <Alert tone="error" onClose={() => setError("")}>{error}</Alert> : null}
          {limitations.map((line) => <Alert tone="warning" key={line}>{line}</Alert>)}
        </div>
      </div>

      {products.length || collections.length || phase === "collections" || phase === "products" ? (
        <div className="si-card">
          <div className="si-card-body">
            <div className="si-stats">
              <Stat icon={<FaLayerGroup />} label={groupsTitle} value={collections.length} />
              <Stat icon={<FaBoxOpen />} label={productMeta.total > products.length ? `Products · ${products.length} loaded` : "Products"} value={Math.max(productMeta.total, products.length)} />
              <Stat icon={<FaSyncAlt />} label="Variants" value={variantCount} />
              <Stat icon={<FaImages />} label="Images" value={imageCount} />
            </div>
          </div>
        </div>
      ) : null}

      {products.length || phase === "products" ? (
        <div className="si-card">
          <div className="si-card-head">
            <h5>Preview</h5>
            <div className="si-search">
              <FaSearch />
              <input className="form-control" placeholder="Search products" value={query} onChange={(event) => { setQuery(event.target.value); setPage(0); }} aria-label="Search preview" />
            </div>
          </div>
          <div className="table-responsive">
            <table className="table si-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>{groups === "categories" ? "Category" : "Collection"}</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((product) => (
                  <ProductRow key={product.id} product={product} group={groupName(product)} />
                ))}
                {showSkeleton ? (
                  [0, 1, 2, 3, 4].map((row) => (
                    <tr key={`skel-${row}`}>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <span className="si-skel" style={{ width: 44, height: 44 }} />
                          <div className="flex-grow-1">
                            <span className="si-skel mb-2" style={{ width: "70%", height: 12 }} />
                            <span className="si-skel" style={{ width: 70, height: 16 }} />
                          </div>
                        </div>
                      </td>
                      {[90, 50, 30, 70].map((width, cell) => (
                        <td key={cell}><span className="si-skel" style={{ width, height: 12 }} /></td>
                      ))}
                    </tr>
                  ))
                ) : null}
                {!visible.length && !showSkeleton ? (
                  <tr>
                    <td colSpan="5">
                      <div className="si-empty">
                        <FaSearch />
                        <div>{searching ? `No loaded products match “${query}”.` : "No products on this page."}</div>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {pageCount > 1 ? (
            <div className="si-pager">
              <span>
                {visible.length ? `Showing ${page * PREVIEW_SIZE + 1}–${page * PREVIEW_SIZE + visible.length} of ${knownTotal}` : `Page ${page + 1} of ${pageCount}`}
                {searching ? ` matches in ${products.length} loaded` : ""}
                {loadingPage ? <span className="ms-2"><Spinner /></span> : null}
              </span>
              <div className="d-flex align-items-center gap-2">
                <span className="d-none d-sm-inline">Page {page + 1} of {pageCount}</span>
                <button type="button" className="si-btn" disabled={page === 0 || loadingPage} onClick={() => setPage(page - 1)}>Previous</button>
                <button type="button" className="si-btn" disabled={!hasNext || loadingPage} onClick={goNext}>
                  {loadingPage ? "Loading…" : "Next"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {ready && !products.length && !collections.length && !busy && !result ? (
        <div className="si-card">
          <div className="si-empty">
            <FaBoxOpen />
            <div className="fw-semibold" style={{ color: "var(--admin-text-main)" }}>Store connected</div>
            <div>Fetch the catalog to preview it, or click Import All to fetch and import in one go.</div>
          </div>
        </div>
      ) : null}

      {result ? (
        <div className="si-card">
          <div className="si-card-head">
            <h5>{phase === "import" ? "Import in progress" : "Import result"}</h5>
            {phase !== "import" && !result.errors.length ? (
              <span className="si-pill is-connected"><FaCheckCircle /> Completed</span>
            ) : null}
          </div>
          <div className="si-card-body">
            <div className="si-stats">
              <Stat icon={<FaLayerGroup />} tone="success" label={`${groupsTitle} added`} value={result.categories_created} />
              <Stat icon={<FaLayerGroup />} label={`${groupsTitle} updated`} value={result.categories_updated} />
              <Stat icon={<FaBoxOpen />} tone="success" label="Products added" value={result.products_created} />
              <Stat icon={<FaSyncAlt />} label="Products updated" value={result.products_updated} />
              <Stat icon={<FaExclamationTriangle />} tone="warning" label="Skipped" value={result.products_skipped} />
              <Stat icon={<FaImages />} label="Images stored" value={result.images_stored} />
            </div>
            {result.errors.length ? (
              <div className="si-alert si-alert-warning">
                <FaExclamationTriangle />
                <div>
                  <div className="fw-semibold mb-1">{result.errors.length} item{result.errors.length === 1 ? "" : "s"} need attention</div>
                  <ul className="si-error-list">
                    {result.errors.slice(0, 50).map((line, index) => <li key={`${index}-${line}`}>{line}</li>)}
                  </ul>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Shopify's "Title / Default Title" option means a product with no real choices.
function isVariable(product) {
  const options = product.options || [];
  if (!options.length) return false;
  return !(options.length === 1 && options[0].name === "Title" && (options[0].values || []).includes("Default Title"));
}

const ProductRow = React.memo(function ProductRow({ product, group }) {
  const variants = product.variants || [];
  const prices = variants.map((variant) => Number(variant.price)).filter((value) => value > 0);
  const price = prices.length ? Math.min(...prices) : 0;
  const stock = variants.reduce((sum, variant) => sum + (Number(variant.qty) || 0), 0);
  const variable = isVariable(product);
  const image = product.images && product.images[0];
  const imageUrl = image && (image.url || image);
  return (
    <tr>
      <td>
        <div className="d-flex align-items-center gap-3">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="si-thumb" loading="lazy" decoding="async" width="44" height="44" />
          ) : (
            <span className="si-thumb si-thumb-empty"><FaImage /></span>
          )}
          <div style={{ minWidth: 0 }}>
            <div className="fw-semibold text-truncate" style={{ maxWidth: 280 }} title={product.title}>{product.title}</div>
            <span className={`si-badge mt-1 ${product.status === "active" ? "is-active" : ""}`}>
              {product.status === "active" ? "Published" : "Draft"}
            </span>
            {product.vendor ? <span className="text-muted small ms-2">{product.vendor}</span> : null}
          </div>
        </div>
      </td>
      <td className="text-muted">{group}</td>
      <td className="fw-semibold" style={{ whiteSpace: "nowrap" }}>{price ? price.toFixed(2) : "—"}</td>
      <td className={stock > 0 ? "" : "text-danger"}>{stock}</td>
      <td>
        <span className={`si-badge ${variable ? "is-variable" : ""}`}>
          {variable ? `${variants.length} variants` : "Simple"}
        </span>
      </td>
    </tr>
  );
});

export function StatusPill({ status, label }) {
  if (status === "loading") {
    return <span className="si-skel" style={{ width: 140, height: 32, borderRadius: 999 }} />;
  }
  const tone = status === "connected" ? "is-connected" : (status === "error" ? "is-error" : "");
  return (
    <span className={`si-pill ${tone}`}>
      <span className="si-pill-dot" />
      {label}
    </span>
  );
}

function Step({ number, label, hint, current }) {
  const done = current > number;
  const active = current === number;
  return (
    <div className={`si-step ${active ? "is-active" : ""} ${done ? "is-done" : ""}`}>
      <span className="si-step-num">{done ? <FaCheck /> : number}</span>
      <div style={{ minWidth: 0 }}>
        <div className="si-step-label">{label}</div>
        <div className="si-step-hint">{hint}</div>
      </div>
    </div>
  );
}

function ProgressPanel({ progress, elapsed }) {
  const known = progress.total > 0;
  const percent = known ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0;
  let eta = "";
  if (known && progress.done > 0 && progress.done < progress.total && elapsed > 2) {
    const remaining = Math.round((elapsed / progress.done) * (progress.total - progress.done));
    eta = `About ${formatDuration(remaining)} left`;
  }
  return (
    <div className="si-card si-progress-card" role="status" aria-live="polite">
      <div className="si-card-body">
        <div className="si-progress-top">
          <div className="si-progress-title">
            <Spinner />
            <span>{progress.label}</span>
          </div>
          {known ? <span className="si-progress-pct">{percent}%</span> : null}
        </div>
        <div className="si-bar">
          <div className={`si-bar-fill ${known ? "" : "is-indeterminate"}`} style={{ width: `${percent}%` }} />
        </div>
        <div className="si-progress-meta">
          <span>
            {progress.done}{known ? ` of ${progress.total}` : ""} {progress.unit}
            {progress.current ? ` · ${progress.current}` : ""}
          </span>
          <span>{formatDuration(elapsed)} elapsed{eta ? ` · ${eta}` : ""}</span>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, tone }) {
  return (
    <div className="si-stat">
      <span className={`si-stat-icon ${tone ? `is-${tone}` : ""}`}>{icon}</span>
      <div>
        <div className="si-stat-value">{value}</div>
        <div className="si-stat-label">{label}</div>
      </div>
    </div>
  );
}

export function Alert({ tone, children, onClose }) {
  const icons = { success: <FaCheckCircle />, error: <FaExclamationTriangle />, warning: <FaInfoCircle /> };
  return (
    <div className={`si-alert si-alert-${tone}`} role={tone === "error" ? "alert" : "status"}>
      {icons[tone]}
      <span>{children}</span>
      {onClose ? (
        <button type="button" className="si-alert-close" onClick={onClose} aria-label="Dismiss"><FaTimes /></button>
      ) : null}
    </div>
  );
}

export function Spinner({ light }) {
  return <span className={`spinner-border spinner-border-sm ${light ? "" : "si-spinner"}`} role="status" aria-hidden="true" />;
}

function formatDuration(seconds) {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes ? `${minutes}m ${String(rest).padStart(2, "0")}s` : `${rest}s`;
}
